import { EVENT_TAG_ICONS } from '@/config/events';
import { isString } from '@/lib/validation';

function isOgEvent(rawEvent) {
  return rawEvent?.kind === 'OG_EVENT'
    || rawEvent?.ogMetadata != null
    || rawEvent?.category?.categoryType === 'OG';
}

function resolveEventPageId(rawEvent) {
  if (isOgEvent(rawEvent)) {
    const eventId = rawEvent?.eventId ?? rawEvent?.ogMetadata?.ogId;

    return eventId != null ? String(eventId) : null;
  }

  if (rawEvent?.idGathering != null) {
    return String(rawEvent.idGathering);
  }

  if (rawEvent?.eventId != null) {
    return rawEvent.eventId;
  }

  if (rawEvent?.id != null) {
    return rawEvent.id;
  }

  return null;
}

function resolveOrganiserUrl(sourceUrl) {
  if (!isString(sourceUrl)) {
    return null;
  }

  const trimmedSourceUrl = sourceUrl.trim();
  if (!trimmedSourceUrl) {
    return null;
  }

  // If the URL already starts with http:// or https://, return it as is
  if (/^https?:\/\//i.test(trimmedSourceUrl)) {
    return trimmedSourceUrl;
  }

  // Reject any other explicit scheme (mailto:, tel:, ftp:, javascript:, …)
  if (/^[a-z][a-z0-9+-]*:/i.test(trimmedSourceUrl)) {
    return null;
  }

  return `https://${trimmedSourceUrl}`;
}

function resolveEventPicture(rawEvent) {
  const images = rawEvent?.images;

  if (images && typeof images === 'object' && !Array.isArray(images)) {
    return {
      mobile: images.small || '',
      tablet: images.medium || '',
      desktop: images.large || '',
    };
  }

  if (Array.isArray(images) && images.length > 0) {
    const first = images[0];
    return {
      mobile: first?.mobile || '',
      tablet: first?.tablet || first?.mobile || '',
      desktop: first?.desktop || first?.mobile || '',
    };
  }

  return {
    mobile: '',
    tablet: '',
    desktop: '',
  };
}

export function useEventDetailsModel() {
  const { useUrl } = useUrls();
  const { useFormatter } = useFormatters();

  const getEventPageUrlFromRawEvent = (rawEvent, rootSlug) => {
    const eventId = resolveEventPageId(rawEvent);
    if (!eventId || !rawEvent?.name) {
      return null;
    }
    const eventNameSlug = useFormatter.normalizeUrl(rawEvent.name);

    return useUrl.getEventPageUrl({
      rootSlug,
      eventId,
      eventNameSlug,
    });
  };

  const buildEventDetailsModel = (rawEvent, schemaPageUrl) => {
    if (!rawEvent) {
      return null;
    }

    const eventPicture = resolveEventPicture(rawEvent);

    const eventPeriod = {};
    if (rawEvent.startDate && rawEvent.endDate) {
      eventPeriod.startDate = rawEvent.startDate;
      eventPeriod.endDate = rawEvent.endDate;
    }

    const cityLabel = rawEvent?.location?.city?.name ?? rawEvent?.location?.cityName ?? '';
    const countryLabel = rawEvent?.location?.country?.name ?? rawEvent?.location?.countryName ?? '';
    const eventLocationName = [cityLabel, countryLabel].filter(Boolean).join(', ');

    const eventPayment = {
      status: rawEvent?.status ? (rawEvent?.status === 'free' ? 'free' : 'paid') : null,
      ticketsLink: resolveOrganiserUrl(rawEvent?.sourceUrl),
    };

    const eventGeoCoordinates = {
      longitude: rawEvent?.location?.geoLocation?.coordinates?.[0] || null,
      latitude: rawEvent?.location?.geoLocation?.coordinates?.[1] || null,
    };

    const eventTags = EVENT_TAG_ICONS.filter((tag) => !!rawEvent?.[tag.id]);

    const eventShortDescription = isString(rawEvent?.description) ? rawEvent.description.trim() : null; // Short description for SEO/Schema purposes
    const eventLongDescription = isString(rawEvent?.hwLongerDescription) ? rawEvent.hwLongerDescription.trim() : null; // Long description for on-page display purposes

    const eventFAQs = Array.isArray(rawEvent?.faq) ? rawEvent.faq : [];

    return {
      id: rawEvent.eventId ?? rawEvent.id,
      eventId: rawEvent.eventId ?? rawEvent.id,
      name: rawEvent.name,
      picture: eventPicture,
      dates: eventPeriod,
      location: rawEvent?.location || null,
      locationName: eventLocationName,
      address: rawEvent?.location?.venue?.name || null,
      geoCoordinates: eventGeoCoordinates,
      payment: eventPayment,
      eventType: rawEvent?.eventType,
      description: eventShortDescription,
      about: eventLongDescription || eventShortDescription || null,
      tags: eventTags,
      faqs: eventFAQs,
      schema: {
        name: rawEvent?.name,
        description: eventShortDescription || eventLongDescription || '',
        startTime: rawEvent?.startDate
          ? `${rawEvent.startDate}${rawEvent?.startTime24h ? `T${rawEvent.startTime24h}` : ''}`
          : undefined,
        endTime: rawEvent?.endDate
          ? `${rawEvent.endDate}${rawEvent?.endTime24h ? `T${rawEvent.endTime24h}` : ''}`
          : undefined,
        host: { name: rawEvent?.location?.city?.name ?? rawEvent?.location?.cityName },
        location: {
          address: rawEvent?.location?.venue?.name,
          city: rawEvent?.location?.city?.name ?? rawEvent?.location?.cityName,
          addressRegion: rawEvent?.location?.venue?.region,
          addressCountry: rawEvent?.location?.venue?.country,
        },
        image: {
          large: eventPicture.desktop,
          small: eventPicture.mobile,
        },
        url: schemaPageUrl,
      },
    };
  };

  return {
    buildEventDetailsModel,
    getEventPageUrlFromRawEvent,
  };
}
